import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { HistoryMessage, PastSession, Usage } from '../models/chatbot.model';
import { ChatbotFacadeService } from './chatbot.facade.service';

@Injectable()
export class ChatbotService {
  constructor(
    private http: HttpClient,
    public facade: ChatbotFacadeService,
    public envFacade: EnvironmentFacadeService,
  ) {}

  private get baseUrl(): string {
    return this.envFacade.isProd ? 'https://alio.inteliqua.net' : 'https://alio.uat.inteliqua.net';
  }

  createSession(): Observable<string> {
    const url = `${this.baseUrl}/createsession`;
    const body = '';

    return from(this.calculateContentSha256(body)).pipe(
      switchMap((contentSha256) => {
        const headers = new HttpHeaders().set('x-amz-content-sha256', contentSha256);
        return this.http.post<any>(url, body, {
          headers,
          withCredentials: true,
          responseType: 'text' as 'json',
        });
      }),
    );
  }

  getSessions(): Observable<PastSession[]> {
    const url = `${this.baseUrl}/sessions`;

    return from(this.calculateContentSha256('')).pipe(
      switchMap((contentSha256) => {
        const headers = new HttpHeaders().set('x-amz-content-sha256', contentSha256);
        return this.http.get<PastSession[]>(url, { headers, withCredentials: true });
      }),
    );
  }

  getHistory(sessionId: string): Observable<HistoryMessage[]> {
    const url = `${this.baseUrl}/history?sessionId=${encodeURIComponent(sessionId)}`;

    return from(this.calculateContentSha256('')).pipe(
      switchMap((contentSha256) => {
        const headers = new HttpHeaders().set('x-amz-content-sha256', contentSha256);
        return this.http.get<HistoryMessage[]>(url, { headers, withCredentials: true });
      }),
    );
  }

  getUsage(): Observable<Usage> {
    const url = `${this.baseUrl}/usage`;

    return from(this.calculateContentSha256('')).pipe(
      switchMap((contentSha256) => {
        const headers = new HttpHeaders().set('x-amz-content-sha256', contentSha256);
        return this.http.get<Usage>(url, { headers, withCredentials: true });
      }),
    );
  }

  postQuestion(threadId: string): Observable<string> {
    const url = `${this.baseUrl}/question`;

    const message = this.facade.message;
    const sessionId = this.facade.sessionId;
    const body = JSON.stringify({ message });

    return from(this.calculateContentSha256(body)).pipe(
      switchMap(
        (contentSha256) =>
          new Observable<string>((observer) => {
            const headers = {
              'x-amz-content-sha256': contentSha256,
              'x-alio-session-id': sessionId,
              'Content-Type': 'application/json',
            };

            const controller = new AbortController();
            this.facade.startTyping(threadId);
            let partial = '';

            fetch(url, {
              method: 'POST',
              headers,
              body,
              credentials: 'include',
              signal: controller.signal,
            })
              .then(async (response) => {
                const reader = response.body!.getReader();
                const decoder = new TextDecoder();

                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  const chunk = decoder.decode(value, { stream: true });
                  partial += chunk;

                  if (partial.includes('<END>')) {
                    const clean = partial.replace('<END>', '');
                    this.facade.updateTypingText(clean);
                    await this.facade.waitForTypingToFinish(clean);
                    observer.next(partial);
                  } else {
                    this.facade.updateTypingText(partial);
                    observer.next(partial);
                  }
                }

                await this.facade.waitForTypingToFinish(partial.replace('<END>', ''));
                this.facade.stopTyping();
                observer.complete();
              })
              .catch((err) => {
                this.facade.stopTyping();
                observer.error(err);
              });

            return () => controller.abort();
          }),
      ),
    );
  }

  calculateContentSha256(body: string): Promise<string> {
    if (!body) {
      return Promise.resolve('e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855');
    }

    const encoder = new TextEncoder();
    const bodyData = encoder.encode(body);

    return crypto.subtle.digest('SHA-256', bodyData).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    });
  }
}
